// ── reports-helpers: module access + report-exclusion + status/download display ─
// Wave 7 (Reports — SECURITY-CRITICAL). Shared by the Reports list / report
// viewer / report-status surfaces. Ids are STRINGS (bigint-safe) — passed
// VERBATIM, never Number()'d.
//
// ── PARTICIPANT GENERATED-REPORT EXCLUSION (FR-REPORT-001, release-blocking) ──
// A `participant` has ZERO visibility of the generated-report taxonomy
// (team_admin / coach_feedback / vjal_internal / ceo_confidential) across EVERY
// surface — route, nav, list, detail, download, status, failure detail. The
// four-type Reports module is for STAFF / coach / company_admin / cohort_lead
// ONLY. This is a fail-closed allow-list: an unknown/new role is denied until it
// is explicitly added. Defense-in-depth at BOTH layers — the route manifest
// denies participant at the route (reportViewRoles → generic-not-found + no nav),
// AND every report page fail-closes via canAccessReportsModule (load-bearing for
// the report read: the read NEVER fires for a participant). A participant must
// not learn a report even EXISTS.

import { humanizeLabel } from "@vjal/ui";

import type { CoachingRole } from "../../domain/roles";
import { filterReadableReports } from "../../domain/sensitive-surfaces";
import type { ReportRowAccessFields } from "../../domain/sensitive-surfaces";
import type { CoachingArtifactDto, CoachingReportType } from "../../services/coaching-dtos";

// ── Reports MODULE access (fail-closed allow-list) ───────────────────────────
// MUST equal the route manifest's reportViewRoles. The biting tests assert that
// participant is NOT here and that the page read never fires for participant.
export const REPORTS_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

/**
 * Whether the role may load the generated-report module (fail-closed). A
 * participant is ALWAYS false (report-exclusion); the report read is gated on
 * this so no report request is made for an excluded role.
 */
export function canAccessReportsModule(role: CoachingRole): boolean {
  return REPORTS_MODULE_ROLES.includes(role);
}

// ── SA-only report regeneration (FR-REPORT-003) ──────────────────────────────
// The regenerate affordance renders ONLY for super_admin (regenerate.reports,
// migration 0225). No other role ever sees it. The POST
// /sessions/:id/reports/regenerate route is NOW LIVE; while the report-agent lane
// is closed the response is an HONEST pending/queued (report:null) — never a
// fabricated 'generated'. See ReportRegenerateSection.
export function canRegenerateReports(role: CoachingRole): boolean {
  return role === "super_admin";
}

// ── Report generation (FR-REPORT) ────────────────────────────────────────────
// POST /sessions/:id/reports/generate is generate.reports = super_admin / admin /
// coach-in-scope. The control renders for those roles; the backend re-applies the
// in-scope guard (a foreign session 404s-via-400). While the report-agent lane is
// closed the response is an HONEST pending/queued (report:null) — never a
// fabricated 'generated'.
export function canGenerateReports(role: CoachingRole): boolean {
  return role === "super_admin" || role === "admin" || role === "coach";
}

// ── A non-empty, non-"new" route segment is the only thing that's a real id. ──
export function isValidReportId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

export function isValidSessionId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

// ── Report STATUS taxonomy (the per-type independent generation status) ──────
// Each report type/session is a distinct status (not_started / queued /
// processing / completed / failed). The backend status enum is looser (e.g.
// `generating`/`ready`); we normalize for display + tone but never invent a
// status the server didn't send.
export type ReportStatusKind = "not_started" | "queued" | "processing" | "completed" | "failed";

const STATUS_ALIASES: Record<string, ReportStatusKind> = {
  not_started: "not_started",
  none: "not_started",
  pending: "queued",
  queued: "queued",
  enqueued: "queued",
  submitted: "processing",
  processing: "processing",
  generating: "processing",
  in_progress: "processing",
  ready: "completed",
  completed: "completed",
  generated: "completed",
  reviewed: "completed",
  approved: "completed",
  failed: "failed",
  error: "failed"
};

/** Normalize a raw backend status to a display kind without fabricating one. */
export function normalizeReportStatus(status: string | null | undefined): ReportStatusKind {
  if (!status) return "not_started";
  return STATUS_ALIASES[status] ?? "processing";
}

export function reportStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "not_started");
}

export function reportStatusTone(status: string | null | undefined): "success" | "warning" | "danger" | "default" {
  switch (normalizeReportStatus(status)) {
    case "completed":
      return "success";
    case "failed":
      return "danger";
    case "processing":
    case "queued":
      return "warning";
    default:
      return "default";
  }
}

/** Whether report content (summary/highlights/actionItems) can be shown yet. */
export function isReportReady(status: string | null | undefined): boolean {
  return normalizeReportStatus(status) === "completed";
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

// ── D-3 access projection (CRITICAL: bridge snake_case DTO → access fields) ──
// The report DTO carries report_type / artifact_visibility / delivery_mode
// (snake_case), but the D-3 evaluator (canRoleReadReport / filterReadableReports)
// reads reportType / visibility / deliveryMode (camelCase). Passing a raw DTO to
// the filter would silently see `reportType: undefined` and treat EVERY row as
// team_admin — leaking coach_feedback / vjal_internal / ceo_confidential into a
// non-staff surface. This projection is the load-bearing bridge; the leak tests
// bite precisely because they pass full DTO rows.
type CoachingArtifactRecord = CoachingArtifactDto & Record<string, unknown>;

/** Read taxonomy fields whether the row is DTO-normalized (snake_case) or live-envelope camelCase. */
export function reportAccessFields(report: CoachingArtifactDto): ReportRowAccessFields {
  const row = report as CoachingArtifactRecord;
  const reportType = report.report_type ?? (row.reportType as CoachingReportType | undefined);
  const deliveryMode =
    report.delivery_mode ??
    (row.deliveryMode as string | undefined) ??
    (reportType === "ceo_confidential" ? "ceo_out_of_band" : reportType ? "in_dashboard" : undefined);
  return {
    reportType,
    visibility: report.artifact_visibility ?? (row.artifactVisibility as string | undefined),
    deliveryMode,
    downloadPolicy: report.download_policy ?? (row.downloadPolicy as string | undefined)
  };
}

/**
 * Drop every report DTO the role may not read per D-3, bridging the snake_case
 * DTO shape to the access-field shape the evaluator expects. Staff see all.
 */
export function filterReadableReportDtos(role: CoachingRole, reports: CoachingArtifactDto[]): CoachingArtifactDto[] {
  // Tag each DTO with the camelCase access fields, run the D-3 filter, then map
  // the survivors back to their original DTOs (preserving identity).
  const tagged = reports.map((report) => ({ report, ...reportAccessFields(report) }));
  return filterReadableReports(role, tagged).map((entry) => entry.report);
}
